import { useState, useEffect, useCallback } from 'react';
import { AxiosError } from 'axios';

/**
 * Rate limit information from API headers
 */
export interface RateLimitInfo {
  /** Current number of requests made */
  used: number;
  /** Maximum requests allowed in window */
  limit: number;
  /** Time until window resets (seconds) */
  resetTime: number;
  /** Percentage of limit used (0-100) */
  percentage: number;
  /** Whether rate limit is approaching (>80%) */
  isApproaching: boolean;
  /** Whether rate limit is exceeded */
  isExceeded: boolean;
}

/**
 * Rate limit status levels
 */
export type RateLimitStatus = 'normal' | 'warning' | 'critical' | 'exceeded';

/**
 * Hook for tracking and managing rate limit information
 */
export const useRateLimit = () => {
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [status, setStatus] = useState<RateLimitStatus>('normal');
  const [timeUntilReset, setTimeUntilReset] = useState<number>(0);

  /**
   * Parse rate limit headers from API response
   */
  const parseRateLimitHeaders = useCallback(
    (headers: Record<string, string>): RateLimitInfo | null => {
      const limit = parseInt(headers['x-ratelimit-limit'] || headers['x-rate-limit-limit'] || '0');
      const remaining = parseInt(
        headers['x-ratelimit-remaining'] || headers['x-rate-limit-remaining'] || '0'
      );
      const reset = parseInt(headers['x-ratelimit-reset'] || headers['x-rate-limit-reset'] || '0');

      if (!limit) return null;

      const used = limit - remaining;
      const percentage = Math.round((used / limit) * 100);
      const resetTime = reset - Math.floor(Date.now() / 1000);

      return {
        used,
        limit,
        resetTime: Math.max(0, resetTime),
        percentage,
        isApproaching: percentage > 80,
        isExceeded: remaining <= 0,
      };
    },
    []
  );

  /**
   * Update rate limit info from response headers
   */
  const updateFromResponse = useCallback(
    (headers: Record<string, string>) => {
      const info = parseRateLimitHeaders(headers);
      if (info) {
        setRateLimitInfo(info);
        setTimeUntilReset(info.resetTime);

        // Update status based on usage
        if (info.isExceeded) {
          setStatus('exceeded');
        } else if (info.percentage >= 90) {
          setStatus('critical');
        } else if (info.percentage > 80) {
          setStatus('warning');
        } else {
          setStatus('normal');
        }
      }
    },
    [parseRateLimitHeaders]
  );

  /**
   * Handle rate limit error (429)
   */
  const handleRateLimitError = useCallback(
    (error: AxiosError) => {
      if (error.response?.status === 429) {
        const headers = error.response.headers as Record<string, string>;
        const retryAfter = parseInt(headers['retry-after'] || '60');

        const info: RateLimitInfo = {
          used: rateLimitInfo?.limit || 100,
          limit: rateLimitInfo?.limit || 100,
          resetTime: retryAfter,
          percentage: 100,
          isApproaching: true,
          isExceeded: true,
        };

        setRateLimitInfo(info);
        setStatus('exceeded');
        setTimeUntilReset(retryAfter);

        return {
          isRateLimited: true,
          retryAfter,
          message:
            (error.response.data as any)?.error?.message ||
            'Too many requests. Please try again later.',
        };
      }

      return { isRateLimited: false };
    },
    [rateLimitInfo]
  );

  /**
   * Get human-readable time until reset
   */
  const getTimeUntilResetText = useCallback((seconds: number): string => {
    if (seconds <= 0) return 'now';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  }, []);

  /**
   * Countdown timer for reset time
   */
  useEffect(() => {
    if (timeUntilReset <= 0) return;

    const timer = setInterval(() => {
      setTimeUntilReset(prev => {
        const newTime = Math.max(0, prev - 1);
        if (newTime === 0 && status === 'exceeded') {
          setStatus('normal');
          setRateLimitInfo(null);
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeUntilReset, status]);

  /**
   * Listen for API responses to update rate limit info
   */
  useEffect(() => {
    const handleApiResponse = (event: CustomEvent) => {
      const { headers } = event.detail;
      updateFromResponse(headers);
    };

    const handleApiError = (event: CustomEvent) => {
      const { status, headers, error } = event.detail;
      if (status === 429) {
        handleRateLimitError(error);
      } else if (headers) {
        updateFromResponse(headers);
      }
    };

    window.addEventListener('api-response', handleApiResponse as EventListener);
    window.addEventListener('api-error', handleApiError as EventListener);

    return () => {
      window.removeEventListener('api-response', handleApiResponse as EventListener);
      window.removeEventListener('api-error', handleApiError as EventListener);
    };
  }, [updateFromResponse, handleRateLimitError]);

  /**
   * Check if user should be warned about rate limits
   */
  const shouldShowWarning = useCallback((): boolean => {
    return status === 'warning' || status === 'critical' || status === 'exceeded';
  }, [status]);

  /**
   * Get status color for UI.
   * All returned values pass WCAG AA (4.5:1) as text on white and as
   * badge/chip backgrounds with white (#FFFFFF) text.
   *   warning  #92400E — 7.14:1 on white (AAA)
   *   critical #991B1B — 7.10:1 on white (AAA)
   *   exceeded #7F1D1D — 9.10:1 on white (AAA)
   *   default  #166534 — 8.59:1 on white (AAA)
   */
  const getStatusColor = useCallback((): string => {
    switch (status) {
      case 'warning':
        return '#92400E'; // Dark amber
      case 'critical':
        return '#991B1B'; // Dark red
      case 'exceeded':
        return '#7F1D1D'; // Darker red
      default:
        return '#166534'; // Dark green
    }
  }, [status]);

  /**
   * Get user-friendly message based on status
   */
  const getStatusMessage = useCallback((): string => {
    if (!rateLimitInfo) return '';

    switch (status) {
      case 'warning':
        return `You've used ${rateLimitInfo.percentage}% of your API quota. Consider slowing down requests.`;
      case 'critical':
        return `You've used ${rateLimitInfo.percentage}% of your API quota. Rate limit will apply soon.`;
      case 'exceeded':
        return `Rate limit exceeded. Please wait ${getTimeUntilResetText(timeUntilReset)} before making more requests.`;
      default:
        return `${rateLimitInfo.used}/${rateLimitInfo.limit} requests used`;
    }
  }, [rateLimitInfo, status, timeUntilReset, getTimeUntilResetText]);

  return {
    rateLimitInfo,
    status,
    timeUntilReset,
    updateFromResponse,
    handleRateLimitError,
    shouldShowWarning,
    getStatusColor,
    getStatusMessage,
    getTimeUntilResetText: () => getTimeUntilResetText(timeUntilReset),
  };
};
